/**
 * Copilot store — global Context provider that owns the single
 * conversation thread used by both the right-side CopilotPanel (mounted
 * in `_layout.tsx`) and the legacy full-page `/app/twin/copilot` route.
 *
 * Lifting the chat state out of the route means navigating between
 * surfaces no longer drops the conversation. The provider also tracks
 * the active page's context (`pageContext`) so an intent like "add a
 * gauge to *this* dashboard" can resolve "this" without forcing the
 * user to spell out the dashboard id.
 *
 * Persistence
 *   - `messages`, `decided`, `aliasMap` are kept in `localStorage` under
 *     `divineiot:copilot-thread` so the user can switch tabs / reload
 *     without losing their place. Capped at 100 messages — older turns
 *     drop off so the store doesn't grow unboundedly.
 *   - `isOpen` is also persisted so the panel restores in whatever state
 *     the user left it.
 *
 * Phase 4 swap point
 *   - Phase 0 mounts `messages` in-memory + localStorage. Phase 4 reads
 *     from `/v1/twin/copilot/sessions/{session_id}` so the conversation
 *     follows the user across browsers + devices.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import {
  FX_COPILOT_MESSAGES,
  type CopilotMessage,
} from "@/lib/widgets/__foundry_fixtures";
import { isLiveApi } from "@/lib/api";
import type { CopilotAbout } from "@/lib/copilot/ask";
import {
  panelReducer,
  type CopilotAskNumbered,
  type CopilotAskRequest,
  type PanelState,
} from "@/lib/copilot/panel-state";

// Live mode starts with an empty thread; the canned foundry demo conversation
// (FX_COPILOT_MESSAGES) is stub/dev-only — seeding it in live leaked the
// foundry demo chat into every org (same cross-tenant fixture leak the Live
// Cleanup closed elsewhere). Stub mode keeps the demo thread.
const SEED_MESSAGES: CopilotMessage[] = isLiveApi ? [] : FX_COPILOT_MESSAGES;

const THREAD_STORAGE_KEY = "divineiot:copilot-thread";
const OPEN_STORAGE_KEY = "divineiot:copilot-open";
const MAX_MESSAGES = 100;

export interface PageCopilotContext {
  /** Path string (e.g. `/app/dashboards/dash-plant-overview`). */
  route: string;
  /** Human label shown in the panel header chip (e.g. "Plant Overview"). */
  label: string;
  /** Dashboard id when the page is a dashboard view — drives "this dashboard" intents. */
  currentDashboardId?: string;
  /** Entity id when the user is on /app/twin and a node is selected. */
  currentEntityId?: string;
  /** Batch / heat number when the user is on a batch detail surface. */
  currentBatchId?: string;
  /** Free-form anchor (e.g. alarm id, correlation pair) for context-aware starters. */
  anchor?: string;
  /**
   * What the page is, as the AI Casting Consultant takes it (lib/copilot/ask, 2026-09-28): the top bar's button opens
   * the consultant with it, so the chip says where the person was. A brief says its cadence and period, a report its
   * window, a dashboard its name.
   */
  about?: CopilotAbout;
}

interface PersistedThread {
  messages: CopilotMessage[];
  decided: Record<string, "accepted" | "rejected">;
  aliasMap: Record<string, string>;
}

export interface CopilotState {
  // Conversation
  messages: CopilotMessage[];
  decided: Record<string, "accepted" | "rejected">;
  aliasMap: Record<string, string>;

  // Panel UI
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /**
   * Opens the panel with a question drafted and what it is about (next phase, step 3): a card's "Ask", the chip over a
   * selection, the top bar's button. Each call is a new request (a new nonce), so the panel starts a fresh conversation
   * with it. Closing the panel forgets it.
   */
  ask: (request: CopilotAskRequest) => void;
  /** The question the panel was last asked with, while it is open; null otherwise (lib/copilot/panel-state). */
  request: CopilotAskNumbered | null;

  // Page context (set by each route's `usePageCopilotContext` hook)
  pageContext: PageCopilotContext | null;
  setPageContext: (ctx: PageCopilotContext | null) => void;

  // Conversation mutations
  appendMessages: (next: CopilotMessage[]) => void;
  setDecision: (proposalId: string, decision: "accepted" | "rejected") => void;
  setAlias: (placeholder: string, realId: string) => void;
  clearThread: () => void;

  /** Mutation counter — drives memoised consumers. */
  version: number;
}

const CopilotContext = createContext<CopilotState | null>(null);

export function useCopilot(): CopilotState {
  const ctx = useContext(CopilotContext);
  if (!ctx) throw new Error("useCopilot must be used inside <CopilotProvider>");
  return ctx;
}

export function useOptionalCopilot(): CopilotState | null {
  return useContext(CopilotContext);
}

function readThread(): PersistedThread | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(THREAD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !Array.isArray(parsed.messages) ||
      typeof parsed.decided !== "object" ||
      typeof parsed.aliasMap !== "object"
    ) {
      return null;
    }
    return parsed as PersistedThread;
  } catch {
    return null;
  }
}

function writeThread(thread: PersistedThread) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THREAD_STORAGE_KEY, JSON.stringify(thread));
  } catch {
    /* swallow */
  }
}

function readOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(OPEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeOpen(open: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(OPEN_STORAGE_KEY, open ? "1" : "0");
  } catch {
    /* swallow */
  }
}

export function CopilotProvider({ children }: { children: ReactNode }) {
  const persisted = readThread();
  const [messages, setMessages] = useState<CopilotMessage[]>(
    () => persisted?.messages ?? SEED_MESSAGES,
  );
  const [decided, setDecided] = useState<Record<string, "accepted" | "rejected">>(
    () => persisted?.decided ?? {},
  );
  const [aliasMap, setAliasMap] = useState<Record<string, string>>(
    () => persisted?.aliasMap ?? {},
  );
  const [panel, dispatch] = useReducer(
    panelReducer,
    undefined,
    (): PanelState => ({ isOpen: readOpen(), request: null, nonce: 0 }),
  );
  const { isOpen, request } = panel;
  const [pageContext, setPageContextState] = useState<PageCopilotContext | null>(null);
  const [version, setVersion] = useState(0);

  // Persist thread on any change.
  useEffect(() => {
    writeThread({ messages, decided, aliasMap });
  }, [messages, decided, aliasMap]);

  useEffect(() => {
    writeOpen(isOpen);
  }, [isOpen]);

  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  const open = useCallback(() => {
    dispatch({ type: "open" });
    bumpVersion();
  }, [bumpVersion]);

  const close = useCallback(() => {
    dispatch({ type: "close" });
    bumpVersion();
  }, [bumpVersion]);

  const toggle = useCallback(() => {
    dispatch({ type: "toggle" });
    bumpVersion();
  }, [bumpVersion]);

  const ask = useCallback<CopilotState["ask"]>(
    (req) => {
      dispatch({ type: "ask", request: req });
      bumpVersion();
    },
    [bumpVersion],
  );

  const setPageContext = useCallback<CopilotState["setPageContext"]>(
    (ctx) => {
      setPageContextState(ctx);
      bumpVersion();
    },
    [bumpVersion],
  );

  const appendMessages = useCallback<CopilotState["appendMessages"]>(
    (next) => {
      setMessages((prev) => {
        const combined = [...prev, ...next];
        // Cap to the last MAX_MESSAGES to keep localStorage small.
        return combined.length > MAX_MESSAGES
          ? combined.slice(combined.length - MAX_MESSAGES)
          : combined;
      });
      bumpVersion();
    },
    [bumpVersion],
  );

  const setDecision = useCallback<CopilotState["setDecision"]>(
    (proposalId, decision) => {
      setDecided((prev) => ({ ...prev, [proposalId]: decision }));
      bumpVersion();
    },
    [bumpVersion],
  );

  const setAlias = useCallback<CopilotState["setAlias"]>(
    (placeholder, realId) => {
      setAliasMap((prev) => ({ ...prev, [placeholder]: realId }));
      bumpVersion();
    },
    [bumpVersion],
  );

  const clearThread = useCallback(() => {
    setMessages(SEED_MESSAGES);
    setDecided({});
    setAliasMap({});
    bumpVersion();
  }, [bumpVersion]);

  const value = useMemo<CopilotState>(
    () => ({
      messages,
      decided,
      aliasMap,
      isOpen,
      open,
      close,
      toggle,
      ask,
      request,
      pageContext,
      setPageContext,
      appendMessages,
      setDecision,
      setAlias,
      clearThread,
      version,
    }),
    [
      messages,
      decided,
      aliasMap,
      isOpen,
      open,
      close,
      toggle,
      ask,
      request,
      pageContext,
      setPageContext,
      appendMessages,
      setDecision,
      setAlias,
      clearThread,
      version,
    ],
  );

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}
